import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

const RegisterPage = ({ onLoginClick, onClose }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    phoneNumber: '+7 ',
    passportSeries: '',
    passportNumber: '',
    registrationAddress: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false); // Управление состоянием диалогового окна

  // Обработка изменений в форме
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Маска телефона: только +7 и 10 цифр
  const handlePhoneChange = (e) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.startsWith('8')) v = '7' + v.slice(1);
    if (v.startsWith('7')) v = v.slice(1);
    if (v.length > 10) v = v.slice(0, 10);
    setFormData((prev) => ({ ...prev, phoneNumber: '+7 ' + v.replace(/(\d{3})(\d{0,3})(\d{0,4})/, (_, a, b, c) => [a, b, c].filter(Boolean).join(' ')) }));
  };

  // Обработка отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    const digits = (formData.phoneNumber || '').replace(/\D/g, '');
    const phoneOk = digits.startsWith('7') ? digits.length === 11 : digits.length === 10;
    if (!phoneOk) {
      setError('Введите корректный номер телефона в формате +7 (10 цифр)');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const payload = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        phoneNumber: formData.phoneNumber.trim(),
        passportSeries: formData.passportSeries.trim() || undefined,
        passportNumber: formData.passportNumber.trim() || undefined,
        registrationAddress: formData.registrationAddress.trim() || undefined,
      };
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка регистрации.');
      }

      const data = await response.json();
      setSuccessMessage(data.message || 'Регистрация прошла успешно. Проверьте вашу почту.');

      // Открываем диалоговое окно
      setDialogOpen(true);

      // Закрываем форму (если передана функция onClose)
      if (onClose) setTimeout(onClose, 500); // Задержка для согласования анимации
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Закрытие диалогового окна
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: 400,
          margin: '0 auto',
          padding: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          backgroundColor: '#F5F3F0',
        }}
      >
        <Typography 
          variant="h5" 
          align="center" 
          gutterBottom
          sx={{ 
            color: '#000000', 
            fontWeight: 'bold',
            fontFamily: 'serif',
          }}
        >
          Регистрация
        </Typography>
        {error && (
          <Typography 
            variant="body2" 
            align="center"
            sx={{ color: '#d32f2f' }}
          >
            {error}
          </Typography>
        )}
        {successMessage && (
          <Typography 
            variant="body2" 
            align="center"
            sx={{ color: '#8B7D6B' }}
          >
            {successMessage}
          </Typography>
        )}
        <TextField
          label="ФИО"
          name="username"
          value={formData.username}
          onChange={handleChange}
          required
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#8B7D6B',
              },
              '&:hover fieldset': {
                borderColor: '#6B5D4B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B7D6B',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#8B7D6B',
            },
          }}
        />
        <TextField
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#8B7D6B',
              },
              '&:hover fieldset': {
                borderColor: '#6B5D4B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B7D6B',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#8B7D6B',
            },
          }}
        />
        <TextField
          label="Телефон"
          value={formData.phoneNumber}
          onChange={handlePhoneChange}
          required
          fullWidth
          placeholder="+7 999 123 45 67"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#8B7D6B',
              },
              '&:hover fieldset': {
                borderColor: '#6B5D4B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B7D6B',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#8B7D6B',
            },
          }}
        />
        <TextField
          label="Пароль"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          required
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#8B7D6B',
              },
              '&:hover fieldset': {
                borderColor: '#6B5D4B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B7D6B',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#8B7D6B',
            },
          }}
        />
        <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5 }}>
          Для более быстрого оформления заказа в центре выдачи вы можете заполнить паспортные данные, это не обязательно.
        </Typography>
        <TextField
          label="Серия паспорта"
          name="passportSeries"
          value={formData.passportSeries}
          onChange={handleChange}
          fullWidth
          placeholder="1234"
          inputProps={{ maxLength: 4 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#8B7D6B',
              },
              '&:hover fieldset': {
                borderColor: '#6B5D4B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B7D6B',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#8B7D6B',
            },
          }}
        />
        <TextField
          label="Номер паспорта"
          name="passportNumber"
          value={formData.passportNumber}
          onChange={handleChange}
          fullWidth
          placeholder="567890"
          inputProps={{ maxLength: 6 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#8B7D6B',
              },
              '&:hover fieldset': {
                borderColor: '#6B5D4B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B7D6B',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#8B7D6B',
            },
          }}
        />
        <TextField
          label="Адрес прописки"
          name="registrationAddress"
          value={formData.registrationAddress}
          onChange={handleChange}
          fullWidth
          multiline
          minRows={2}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#8B7D6B',
              },
              '&:hover fieldset': {
                borderColor: '#6B5D4B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B7D6B',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#8B7D6B',
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          fullWidth
          sx={{
            backgroundColor: '#8B7D6B',
            color: '#FFFFFF',
            fontWeight: 'bold',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#6B5D4B',
            },
            '&:disabled': {
              backgroundColor: '#8B7D6B',
              opacity: 0.6,
            },
          }}
        >
          {loading ? 'Регистрация...' : 'Зарегистрироваться'}
        </Button>
        <Typography variant="body2" align="center" sx={{ mt: 2, color: '#000000' }}>
          Уже есть аккаунт?{' '}
          <Button
            variant="text"
            onClick={onLoginClick}
            sx={{ 
              textTransform: 'none', 
              p: 0,
              color: '#8B7D6B',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'rgba(139, 125, 107, 0.1)',
              },
            }}
          >
            Войти
          </Button>
        </Typography>
      </Box>

      {/* Диалоговое окно */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        PaperProps={{
          sx: {
            backgroundColor: '#F5F3F0',
          },
        }}
      >
        <DialogTitle sx={{ color: '#000000', fontWeight: 'bold', fontFamily: 'serif' }}>
          Регистрация завершена
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#000000' }}>
            Регистрация прошла успешно. Письмо с подтверждением отправлено на вашу электронную почту.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDialogClose}
            sx={{
              backgroundColor: '#8B7D6B',
              color: '#FFFFFF',
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#6B5D4B',
              },
            }}
          >
            Ок
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegisterPage;
